import React, { useState, useMemo } from 'react';
import { IHighlight } from './types';
import ArrowIcon from 'assets/svg/left.svg';
import { TextLabel, SmallText } from 'ds/components/typography';
import { useLocale } from 'locale';
import { insightTypeToIcon } from 'helpers/insight';
import { FadeWrapper } from 'components/authentication/styled';
import { getInfinitePrevIndex, getInfiniteNextIndex } from 'utils/gallery';
import { truncate } from 'lodash';
import { Separator } from 'components/navigation/header/styled';
import {
  HighlightsPagerControlsInner,
  SwitchHighlightButtonWrapper,
  SwitchHighlightPreviewWrapper,
  SwitchHighlightPreviewIcon,
  SwitchHighlightCounter,
} from './styled';

export type SwitchAction = 'next' | 'prev';

interface HighlightsPagerControlsProps {
  highlights: IHighlight[];
  onPrevIndex: () => void;
  onNextIndex: () => void;
  activeHighlightIndex: number;
  isCommercial: boolean;
}

type HoveredButtonStatus = 'none' | 'next' | 'prev';

const HighlightsPagerControls: React.FunctionComponent<HighlightsPagerControlsProps> = (props) => {
  const { highlights, onPrevIndex, onNextIndex, activeHighlightIndex, isCommercial } = props;
  const [ hoveredButton, setHoveredButton ] = useState('none');
  const { t } = useLocale();
  const activeHighlight = highlights[activeHighlightIndex];
  const activeGroupHighlights = useMemo(() => highlights.filter(h => h.group === activeHighlight.group), [ activeHighlight ]);
  const prevHighlight = highlights[getInfinitePrevIndex(activeHighlightIndex, highlights.length)];
  const nextHighlight = highlights[getInfiniteNextIndex(activeHighlightIndex, highlights.length)];

  const makeHoveredButtonHandler = (nextState: HoveredButtonStatus) => () => setHoveredButton(nextState);

  return (
    <>
      <HighlightsPagerControlsInner>
        <SwitchHighlightButtonWrapper
          action="prev"
          onClick={onPrevIndex}
          insightType={prevHighlight.type}
          onMouseEnter={makeHoveredButtonHandler('prev')}
          onMouseLeave={makeHoveredButtonHandler('none')}
          data-auto="insight-controller-prev"
        >
          <ArrowIcon width={24} height={24} />
        </SwitchHighlightButtonWrapper>
        <FadeWrapper isVisible={hoveredButton === 'prev'} transitionTime={300} deferTime={100}>
          <SwitchHighlightPreviewWrapper action="prev" insightType={prevHighlight.type}>
            <SwitchHighlightPreviewIcon action="prev" insightType={prevHighlight.type}>{insightTypeToIcon[prevHighlight.type]}</SwitchHighlightPreviewIcon>
            <SmallText>{truncate(prevHighlight.previewText, { length: 45 })}</SmallText>
          </SwitchHighlightPreviewWrapper>
        </FadeWrapper>
        <FadeWrapper isVisible={hoveredButton === 'none'}>
          <SwitchHighlightCounter>
            <TextLabel
              caps
              data-auto="desktop-opened-tradeoffs-indexes"
              data-auto-opened-tradeoff-index={activeGroupHighlights.indexOf(activeHighlight)}
            >
              {t('expandedHighlight.highlightGroupLabel', {
                group: activeHighlight.group,
                isCommercial,
              })}
              <Separator />
              {activeGroupHighlights.indexOf(activeHighlight) + 1}/{activeGroupHighlights.length}
            </TextLabel>
          </SwitchHighlightCounter>
        </FadeWrapper>
        <FadeWrapper isVisible={hoveredButton === 'next'} transitionTime={300} deferTime={100}>
          <SwitchHighlightPreviewWrapper action="next" insightType={nextHighlight.type}>
            <SwitchHighlightPreviewIcon action="next" insightType={nextHighlight.type}>{insightTypeToIcon[nextHighlight.type]}</SwitchHighlightPreviewIcon>
            <SmallText>{truncate(nextHighlight.previewText, { length: 45 })}</SmallText>
          </SwitchHighlightPreviewWrapper>
        </FadeWrapper>
        <SwitchHighlightButtonWrapper
          action="next"
          onClick={onNextIndex}
          insightType={nextHighlight.type}
          onMouseEnter={makeHoveredButtonHandler('next')}
          onMouseLeave={makeHoveredButtonHandler('none')}
          data-auto="insight-controller-next"
        >
          <ArrowIcon width={24} height={24} />
        </SwitchHighlightButtonWrapper>
      </HighlightsPagerControlsInner>
    </>
  );
};

export default HighlightsPagerControls;
